import { useState, type FormEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MailProvider } from '@mailpilot/shared';
import { ChevronDown, ExternalLink, FlaskConical, KeyRound, Loader2, Lock, Plus, Settings2 } from 'lucide-react';
import { AolLogo, FastmailLogo, GmailLogo, GoogleG, ICloudLogo, MicrosoftLogo, OtherMailLogo, OutlookLogo, YahooLogo, ZohoLogo } from './ProviderLogos';
import { api, type BodyOf, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { isValidEmail } from '../../lib/format';
import { Badge, Button, Card, CardBody, CardHeader, Checkbox, Field, InlineError, Input, Modal, Notice, useToast } from '../ui';
import { MicroLabel, SubSection, Well } from './SettingsPrimitives';
import { CapabilityList } from './AccountsShared';
import type { useOAuthConnect } from './AccountsOAuth';

type ProviderInfo = ResOf<'GET /api/mailboxes'>['providers'][number];

/** Providers that connect with IMAP + an app password, with their server settings pre-filled. */
export interface ImapPreset {
  id: string;
  name: string;
  logo: ReactNode;
  imapHost: string;
  imapPort: number;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  /** Where to create the app password, and how. */
  appPasswordUrl?: string;
  steps?: string[];
}

const PRESETS: ImapPreset[] = [
  {
    id: 'icloud',
    name: 'iCloud Mail',
    logo: <ICloudLogo />,
    imapHost: 'imap.mail.me.com',
    imapPort: 993,
    smtpHost: 'smtp.mail.me.com',
    smtpPort: 587,
    smtpSecure: false,
    appPasswordUrl: 'https://account.apple.com/account/manage',
    steps: ['Sign in at account.apple.com', 'Open “Sign-In and Security” → “App-Specific Passwords”', 'Click “+”, name it “MailPilot”, and copy the password'],
  },
  {
    id: 'yahoo',
    name: 'Yahoo Mail',
    logo: <YahooLogo />,
    imapHost: 'imap.mail.yahoo.com',
    imapPort: 993,
    smtpHost: 'smtp.mail.yahoo.com',
    smtpPort: 465,
    smtpSecure: true,
    appPasswordUrl: 'https://login.yahoo.com/myaccount/security/app-password',
    steps: ['Open Yahoo Account Security', 'Choose “Generate app password”', 'Name it “MailPilot” and copy the password'],
  },
  {
    id: 'zoho',
    name: 'Zoho Mail',
    logo: <ZohoLogo />,
    imapHost: 'imap.zoho.com',
    imapPort: 993,
    smtpHost: 'smtp.zoho.com',
    smtpPort: 465,
    smtpSecure: true,
    appPasswordUrl: 'https://accounts.zoho.com/home#security/app_password',
    steps: ['In Zoho Mail settings, turn on IMAP access', 'Open Zoho Accounts → Security → App Passwords', 'Generate one called “MailPilot” and copy it'],
  },
  {
    id: 'fastmail',
    name: 'Fastmail',
    logo: <FastmailLogo />,
    imapHost: 'imap.fastmail.com',
    imapPort: 993,
    smtpHost: 'smtp.fastmail.com',
    smtpPort: 465,
    smtpSecure: true,
    appPasswordUrl: 'https://app.fastmail.com/settings/security/apps',
    steps: ['Open Settings → Privacy & Security → Manage app passwords', 'Create a new app password with “Mail (IMAP/POP/SMTP)” access', 'Copy the password'],
  },
  {
    id: 'aol',
    name: 'AOL Mail',
    logo: <AolLogo />,
    imapHost: 'imap.aol.com',
    imapPort: 993,
    smtpHost: 'smtp.aol.com',
    smtpPort: 465,
    smtpSecure: true,
    appPasswordUrl: 'https://login.aol.com/account/security/app-passwords',
    steps: ['Open AOL Account Security', 'Choose “Generate app password”', 'Name it “MailPilot” and copy the password'],
  },
  { id: 'other', name: 'Other (IMAP)', logo: <OtherMailLogo />, imapHost: '', imapPort: 993, smtpHost: '', smtpPort: 465, smtpSecure: true },
];

/** Compact "needs server setup" note with the technical details on demand. */
function SetupNeeded({ hint }: { hint: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 text-left">
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 rounded-full bg-warn-soft px-2.5 py-1 text-xs font-semibold text-warn-ink hover:brightness-95">
        <Settings2 className="h-3.5 w-3.5" /> Needs server setup
        <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <p className="mt-2 rounded-xl bg-surface p-3 text-xs text-muted [overflow-wrap:anywhere]">{hint ?? 'The server administrator needs to configure this provider before it can be used.'}</p>}
    </div>
  );
}

function BigProviderCard({
  logo,
  title,
  blurb,
  button,
  info,
  loading,
  onConnect,
}: {
  logo: ReactNode;
  title: string;
  blurb: string;
  button: ReactNode;
  info: ProviderInfo;
  loading: boolean;
  onConnect: () => void;
}) {
  const [caps, setCaps] = useState(false);
  return (
    <div className={clsx('flex flex-col items-center rounded-2xl bg-raised/80 p-6 text-center transition-colors hover:bg-raised', !info.configured && 'opacity-90')}>
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-surface shadow-sm ring-1 ring-line/60">{logo}</div>
      <h3 className="mt-4 text-[0.9375rem] font-semibold text-ink">{title}</h3>
      <p className="mt-1 text-[0.8125rem] text-muted">{blurb}</p>
      <div className="mt-5 w-full">
        <button
          type="button"
          disabled={!info.configured || loading}
          onClick={onConnect}
          className="flex h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-[#dadce0] bg-white px-4 text-[0.875rem] font-semibold text-[#1f1f1f] shadow-sm transition-[box-shadow,background-color] hover:bg-[#f8faff] hover:shadow-md active:bg-[#eef3fd] disabled:cursor-not-allowed disabled:opacity-55 dark:border-[#8e918f]"
        >
          {loading ? <Loader2 className="h-[18px] w-[18px] animate-spin text-[#5f6368]" /> : null}
          {button}
        </button>
      </div>
      {!info.configured && <SetupNeeded hint={info.setupHint} />}
      <button type="button" onClick={() => setCaps((c) => !c)} className="mt-3 text-xs font-medium text-muted hover:text-primary-ink">
        {caps ? 'Hide details' : 'What MailPilot can do'}
      </button>
      {caps && (
        <div className="mt-3 w-full border-t border-line/70 pt-3 text-left">
          <CapabilityList caps={info.capabilities} compact />
        </div>
      )}
    </div>
  );
}

export function AccountsAddSection({
  providers,
  oauth,
  emptyState,
}: {
  providers: ProviderInfo[];
  oauth: ReturnType<typeof useOAuthConnect>;
  emptyState?: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [preset, setPreset] = useState<ImapPreset | null>(null);

  const demo = useMutation({
    mutationFn: () => api('POST /api/mailboxes/demo', { body: { label: 'Demo' } }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.mailboxes });
      toast.success(`Demo mailbox ${res.mailbox.email} added`);
    },
    onError: (e) => toast.error(e),
  });

  const by = new Map(providers.map((p) => [p.provider, p]));
  const gmail = by.get('gmail');
  const outlook = by.get('outlook');
  const imap = by.get('imap');
  const demoInfo = by.get('demo');

  return (
    <Card id="add-account" className="scroll-mt-6">
      <CardHeader
        icon={<Plus className="h-5 w-5" aria-hidden />}
        title={emptyState ? 'Connect your first email account' : 'Add an email account'}
        description={emptyState ? 'Pick your email provider. Gmail and Outlook sign in with their own secure page — MailPilot never sees your password.' : 'Each account keeps its own colour, sender identity and rules.'}
      />
      <CardBody className="space-y-6">
        {oauth.polling && (
          <Notice tone="info" icon={<Loader2 className="h-4 w-4 animate-spin" aria-hidden />}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>Waiting for you to finish signing in with your browser…</span>
              <button type="button" className="font-semibold underline underline-offset-2" onClick={oauth.stopPolling}>
                Stop waiting
              </button>
            </div>
          </Notice>
        )}

        {(gmail || outlook) && (
          <div className="grid gap-4 sm:grid-cols-2">
            {gmail && (
              <BigProviderCard
                logo={<GmailLogo className="h-9 w-9" />}
                title="Gmail"
                blurb="Gmail and Google Workspace accounts"
                info={gmail}
                loading={oauth.pending === 'gmail'}
                onConnect={() => void oauth.start('gmail')}
                button={
                  <>
                    <GoogleG /> Continue with Google
                  </>
                }
              />
            )}
            {outlook && (
              <BigProviderCard
                logo={<OutlookLogo className="h-9 w-9" />}
                title="Outlook"
                blurb="Outlook.com, Hotmail and Microsoft 365"
                info={outlook}
                loading={oauth.pending === 'outlook'}
                onConnect={() => void oauth.start('outlook')}
                button={
                  <>
                    <MicrosoftLogo /> Continue with Microsoft
                  </>
                }
              />
            )}
          </div>
        )}

        {imap && (
          <div>
            <MicroLabel className="mb-3 flex items-center gap-3">
              <span className="h-px flex-1 bg-line/70" aria-hidden /> Other email providers <span className="h-px flex-1 bg-line/70" aria-hidden />
            </MicroLabel>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={!imap.configured}
                  onClick={() => setPreset(p)}
                  className="group flex flex-col items-center gap-2 rounded-2xl bg-raised/80 px-2 py-3.5 text-center transition-colors hover:bg-primary-soft disabled:opacity-50 disabled:hover:bg-raised/80"
                >
                  <span className="transition-transform group-hover:scale-110">{p.logo}</span>
                  <span className="text-[0.75rem] font-medium leading-tight text-ink">{p.name}</span>
                </button>
              ))}
            </div>
            <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-muted">
              <KeyRound className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                These providers don't offer a sign-in button for other apps, so they connect with an <b className="font-semibold text-ink">app password</b> — a separate code you create in that account's security settings. We'll show you how.
              </span>
            </p>
          </div>
        )}

        {demoInfo?.configured && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-raised/80 p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-info-soft text-info-ink">
                <FlaskConical className="h-[18px] w-[18px]" aria-hidden />
              </span>
              <div>
                <p className="flex items-center gap-2 text-[0.8125rem] font-semibold text-ink">
                  Demo mailbox <Badge tone="info">Demo</Badge>
                </p>
                <p className="text-xs text-muted">{PROVIDER_BLURB.demo}</p>
              </div>
            </div>
            <Button size="sm" loading={demo.isPending} onClick={() => demo.mutate()}>
              Add demo mailbox
            </Button>
          </div>
        )}

        <p className="flex items-center gap-2 text-xs text-muted">
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden /> Sign-in tokens and app passwords are encrypted on the server and never shown again.
        </p>
      </CardBody>
      <ImapModal key={preset?.id ?? 'none'} preset={preset} onClose={() => setPreset(null)} />
    </Card>
  );
}

const PROVIDER_BLURB: Record<MailProvider, string> = {
  gmail: 'Sign in with Google. MailPilot never sees your Google password.',
  outlook: 'Sign in with Microsoft. MailPilot never sees your Microsoft password.',
  imap: 'For iCloud, Yahoo, Fastmail and other providers that support IMAP and SMTP.',
  demo: 'Simulated sample mail for trying MailPilot. Not a live connection.',
};

type ImapBody = BodyOf<'POST /api/mailboxes/imap'>;
const EMPTY_IMAP: ImapBody = {
  email: '',
  displayName: '',
  senderName: '',
  imapHost: '',
  imapPort: 993,
  imapSecure: true,
  smtpHost: '',
  smtpPort: 465,
  smtpSecure: true,
  username: '',
  password: '',
};

function ImapModal({ preset, onClose }: { preset: ImapPreset | null; onClose: () => void }) {
  const open = !!preset;
  const qc = useQueryClient();
  const toast = useToast();
  const known = !!preset && preset.id !== 'other';
  const [showServers, setShowServers] = useState(!known);
  const [f, setF] = useState<ImapBody>(() =>
    preset ? { ...EMPTY_IMAP, imapHost: preset.imapHost, imapPort: preset.imapPort, smtpHost: preset.smtpHost, smtpPort: preset.smtpPort, smtpSecure: preset.smtpSecure } : EMPTY_IMAP,
  );
  const [touched, setTouched] = useState(false);
  const set = <K extends keyof ImapBody>(k: K, v: ImapBody[K]) => setF((p) => ({ ...p, [k]: v }));

  const save = useMutation({
    mutationFn: (body: ImapBody) => api('POST /api/mailboxes/imap', { body }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.mailboxes });
      toast.success(`${res.mailbox.email} connected`);
      setF(EMPTY_IMAP);
      setTouched(false);
      onClose();
    },
  });

  const emailErr = touched && !isValidEmail(f.email) ? 'Enter a valid email address' : undefined;
  const required = (v: string) => (touched && !v.trim() ? 'Required' : undefined);
  const valid = isValidEmail(f.email) && f.imapHost.trim() && f.smtpHost.trim() && f.password && f.imapPort > 0 && f.smtpPort > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    save.mutate({ ...f, username: f.username.trim() || f.email.trim(), displayName: f.displayName.trim() || f.email.trim() });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={known ? `Connect ${preset!.name}` : 'Connect an IMAP / SMTP mailbox'}
      description={known ? 'Enter your address and an app password. The server settings are filled in for you.' : "Use your provider's server settings. MailPilot checks the connection before saving."}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="imap-form" loading={save.isPending}>
            Connect mailbox
          </Button>
        </>
      }
    >
      <form id="imap-form" onSubmit={submit} className="space-y-5" autoComplete="off">
        {known && preset!.steps ? (
          <Well className="flex items-start gap-4">
            <span className="shrink-0">{preset!.logo}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[0.8125rem] font-semibold text-ink">First, create an app password</p>
              <ol className="mt-1.5 list-decimal space-y-0.5 pl-4 text-[0.8125rem] text-muted">
                {preset!.steps.map((st) => (
                  <li key={st}>{st}</li>
                ))}
              </ol>
              {preset!.appPasswordUrl && (
                <a href={preset!.appPasswordUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[0.8125rem] font-semibold text-primary-ink hover:underline">
                  Open {preset!.name} security settings <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              )}
            </div>
          </Well>
        ) : (
          <Notice tone="info" icon={<KeyRound className="h-4 w-4" aria-hidden />}>
            <p>
              <strong>Gmail and Outlook should use their sign-in buttons</strong> — never enter those passwords here.
            </p>
            <p className="mt-1">Most providers need an <strong>app password</strong> created in your account's security settings; your normal password usually won't work.</p>
          </Notice>
        )}
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Email address" required error={emailErr}>
            {(id) => <Input id={id} type="email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" />}
          </Field>
          <Field label="Account name" hint='Shown in MailPilot, e.g. "Personal"'>
            {(id) => <Input id={id} value={f.displayName} onChange={(e) => set('displayName', e.target.value)} />}
          </Field>
          <Field label="Sender name" hint="Name recipients see in the From line">
            {(id) => <Input id={id} value={f.senderName} onChange={(e) => set('senderName', e.target.value)} />}
          </Field>
          <Field label="Username" hint="Usually your full email address">
            {(id) => <Input id={id} value={f.username} autoComplete="off" onChange={(e) => set('username', e.target.value)} placeholder={f.email || 'you@example.com'} />}
          </Field>
        </div>
        <Field label="App password" required error={required(f.password)} hint="Stored encrypted on the server and never shown again.">
          {(id) => <Input id={id} type="password" autoComplete="off" value={f.password} onChange={(e) => set('password', e.target.value)} placeholder={known ? 'xxxx-xxxx-xxxx-xxxx' : undefined} />}
        </Field>
        {known && (
          <button type="button" aria-expanded={showServers} onClick={() => setShowServers((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-muted hover:text-ink">
            <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', showServers && 'rotate-180')} aria-hidden /> {showServers ? 'Hide' : 'Show'} server settings
          </button>
        )}
        {showServers && (
          <div className="space-y-6 border-t border-line/70 pt-5">
            <SubSection title="Incoming mail (IMAP)">
              <div className="grid gap-5 sm:grid-cols-[1fr_8rem]">
                <Field label="IMAP host" required error={required(f.imapHost)}>
                  {(id) => <Input id={id} value={f.imapHost} onChange={(e) => set('imapHost', e.target.value)} placeholder="imap.mail.me.com" />}
                </Field>
                <Field label="Port">
                  {(id) => <Input id={id} type="number" min={1} max={65535} value={f.imapPort} onChange={(e) => set('imapPort', Number(e.target.value))} />}
                </Field>
              </div>
              <Checkbox checked={f.imapSecure} onChange={(v) => set('imapSecure', v)} label="Use SSL/TLS" />
            </SubSection>
            <SubSection title="Outgoing mail (SMTP)">
              <div className="grid gap-5 sm:grid-cols-[1fr_8rem]">
                <Field label="SMTP host" required error={required(f.smtpHost)}>
                  {(id) => <Input id={id} value={f.smtpHost} onChange={(e) => set('smtpHost', e.target.value)} placeholder="smtp.mail.me.com" />}
                </Field>
                <Field label="Port">
                  {(id) => <Input id={id} type="number" min={1} max={65535} value={f.smtpPort} onChange={(e) => set('smtpPort', Number(e.target.value))} />}
                </Field>
              </div>
              <Checkbox checked={f.smtpSecure} onChange={(v) => set('smtpSecure', v)} label="Use SSL/TLS" description="Turn off for STARTTLS on port 587." />
            </SubSection>
          </div>
        )}
        <InlineError error={save.error} />
      </form>
    </Modal>
  );
}
